import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button, Dropdown, Form, Header, Icon, Message, Segment, Statistic,
} from "semantic-ui-react";
import apis from "../../../apis";
import ComparisonView from "./ComparisonView";
import SubjectHistory from "./SubjectHistory";
import AutomationIncidents from "./AutomationIncidents";
import AutomationRuns from "./AutomationRuns";
import StageTiming from "./StageTiming";
import {
  formatDuration, formatRate, isValidPeriod, toDateTimeInputValue,
} from "../formatters";

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 10;
const RUN_STATES = ["running", "stuck", "succeeded", "failed", "cancelled"];

function initialPeriod() {
  const to = new Date();
  return { from: new Date(to.getTime() - 7 * DAY_MS), to };
}

const AutomationDetail = ({ automationId, onBack, onOpenRun }) => {
  const startingPeriod = initialPeriod();
  const [period, setPeriod] = useState(startingPeriod);
  const [fromInput, setFromInput] = useState(toDateTimeInputValue(startingPeriod.from));
  const [toInput, setToInput] = useState(toDateTimeInputValue(startingPeriod.to));
  const [workflowVersion, setWorkflowVersion] = useState("");
  const [runState, setRunState] = useState("");
  const [runPage, setRunPage] = useState(1);
  const [incidentPage, setIncidentPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [runs, setRuns] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [runsError, setRunsError] = useState("");
  const [incidentsError, setIncidentsError] = useState("");
  const detailRequestGeneration = useRef(0);
  const runsRequestGeneration = useRef(0);
  const incidentsRequestGeneration = useRef(0);
  const selectedWorkflowVersion = detail?.selectedWorkflowVersion;

  const loadDetail = useCallback(async () => {
    const generation = ++detailRequestGeneration.current;
    setLoading(true);
    const rangeParams = { from: period.from.toISOString(), to: period.to.toISOString() };
    const requestedVersionParams = workflowVersion ? { workflowVersion } : {};
    try {
      const detailResponse = await apis.automationMonitoring.getAutomationDetail(
        automationId,
        { ...rangeParams, ...requestedVersionParams },
      );
      if (generation !== detailRequestGeneration.current) return;
      setDetail(detailResponse.data);
      setDetailError("");
    } catch (requestError) {
      if (generation === detailRequestGeneration.current) {
        setDetailError(requestError.response?.data?.message || "Could not load this automation.");
      }
    } finally {
      if (generation === detailRequestGeneration.current) setLoading(false);
    }
  }, [automationId, period, workflowVersion]);

  const loadRuns = useCallback(async () => {
    if (!selectedWorkflowVersion) return;
    const generation = ++runsRequestGeneration.current;
    setRuns(null);
    try {
      const { data } = await apis.automationMonitoring.getAutomationRuns(automationId, {
        from: period.from.toISOString(),
        to: period.to.toISOString(),
        workflowVersion: selectedWorkflowVersion,
        ...(runState ? { state: runState } : {}),
        page: runPage,
        limit: PAGE_SIZE,
      });
      if (generation !== runsRequestGeneration.current) return;
      setRuns(data);
      setRunsError("");
    } catch (requestError) {
      if (generation === runsRequestGeneration.current) {
        setRunsError(requestError.response?.data?.message || "Could not load automation runs.");
      }
    }
  }, [automationId, period, runPage, runState, selectedWorkflowVersion]);

  const loadIncidents = useCallback(async () => {
    if (!selectedWorkflowVersion) return;
    const generation = ++incidentsRequestGeneration.current;
    setIncidents(null);
    try {
      const { data } = await apis.automationMonitoring.getIncidents({
        automationId,
        workflowVersion: selectedWorkflowVersion,
        page: incidentPage,
        limit: 5,
      });
      if (generation !== incidentsRequestGeneration.current) return;
      setIncidents(data);
      setIncidentsError("");
    } catch (requestError) {
      if (generation === incidentsRequestGeneration.current) {
        setIncidentsError(requestError.response?.data?.message || "Could not load automation incidents.");
      }
    }
  }, [automationId, incidentPage, selectedWorkflowVersion]);

  useEffect(() => {
    loadDetail();
    return () => {
      detailRequestGeneration.current += 1;
    };
  }, [loadDetail]);

  useEffect(() => {
    loadRuns();
    return () => {
      runsRequestGeneration.current += 1;
    };
  }, [loadRuns]);

  useEffect(() => {
    loadIncidents();
    return () => {
      incidentsRequestGeneration.current += 1;
    };
  }, [loadIncidents]);

  const applyPeriod = (event) => {
    event.preventDefault();
    const from = new Date(fromInput);
    const to = new Date(toInput);
    if (!isValidPeriod(from, to)) {
      setDetailError("Choose a valid period of 31 days or less.");
      return;
    }
    setRunPage(1);
    setPeriod({ from, to });
  };

  const refresh = () => {
    loadDetail();
    loadRuns();
    loadIncidents();
  };

  const workflowOptions = (detail?.workflowVersions || []).map((version) => ({ key: version, value: version, text: version }));
  const runStateOptions = RUN_STATES.map((state) => ({ key: state, value: state, text: state }));
  const stages = detail?.stages || [];
  const summary = detail?.summary;
  const error = detailError || runsError || incidentsError;

  return (
    <div className="automation-monitoring">
      <Segment className="automation-monitoring__header" inverted>
        <div>
          <Header as="h1">{detail?.automation?.name || "Automation detail"}</Header>
          <span>{detail?.automation?.slug || "Run history and stage timing"}</span>
        </div>
        <div>
          <Button onClick={onBack}><Icon name="arrow left" />Back</Button>
          <Button color="teal" loading={loading} onClick={refresh}><Icon name="refresh" />Refresh</Button>
        </div>
      </Segment>

      {error ? <Message negative content={error} /> : null}
      {loading && !detail ? <Message content="Loading automation detail..." /> : null}
      {detail?.automation && !detail.automation.enabled ? <Message warning content="This automation is disabled." /> : null}

      {detail ? (
        <>
          <Segment as="form" className="automation-monitoring__filters" onSubmit={applyPeriod}>
            <Form.Input label="From" type="datetime-local" value={fromInput} onChange={(event) => setFromInput(event.target.value)} />
            <Form.Input label="To" type="datetime-local" value={toInput} onChange={(event) => setToInput(event.target.value)} />
            <Dropdown
              selection
              aria-label="Workflow version"
              options={workflowOptions}
              value={workflowVersion || detail.selectedWorkflowVersion}
              onChange={(event, data) => { setWorkflowVersion(data.value); setRunPage(1); setIncidentPage(1); }}
            />
            <Button type="submit">Apply period</Button>
          </Segment>
          {detail.workflowVersionsTruncated ? (
            <Message warning content={`Workflow list is limited to ${detail.workflowVersionLimit} versions.`} />
          ) : null}

          {summary ? (
            <Statistic.Group widths="five" size="small" className="automation-monitoring__metrics">
              <Statistic><Statistic.Value>{summary.total}</Statistic.Value><Statistic.Label>Runs</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{formatRate(summary.completionRate)}</Statistic.Value><Statistic.Label>Completion rate</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{formatRate(summary.failureRate)}</Statistic.Value><Statistic.Label>Failure rate</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{formatDuration(summary.duration.medianDurationMs)}</Statistic.Value><Statistic.Label>Median</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{formatDuration(summary.duration.p95DurationMs)}</Statistic.Value><Statistic.Label>P95</Statistic.Label></Statistic>
            </Statistic.Group>
          ) : null}

          <StageTiming
            stages={stages}
            truncated={detail.stagesTruncated}
            resultLimit={detail.stageResultLimit}
          />
          <AutomationRuns
            automationId={automationId}
            runs={runs}
            page={runPage}
            state={runState}
            stateOptions={runStateOptions}
            onOpenRun={onOpenRun}
            onPageChange={setRunPage}
            onStateChange={(value) => { setRunState(value); setRunPage(1); }}
          />
          <AutomationIncidents incidents={incidents} page={incidentPage} onPageChange={setIncidentPage} />
          <ComparisonView automationId={automationId} workflowVersion={detail.selectedWorkflowVersion} />
          <SubjectHistory defaultEnvironment={detail.automation.environment} onOpenRun={onOpenRun} />
        </>
      ) : null}
    </div>
  );
};

AutomationDetail.propTypes = {
  automationId: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onOpenRun: PropTypes.func.isRequired,
};

export { chartData } from "./StageTiming";
export default AutomationDetail;
